import React from 'react';
import { MapPin, Home } from 'lucide-react';
import CountrySelect from './CountrySelect';

interface AddressValues {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
}

interface Props {
  values: AddressValues;
  onChange: (values: AddressValues) => void;
  onBlur?: () => void;
  disabled?: boolean;
  /** Show icon labels (for the confirm screen) */
  showIcons?: boolean;
}

export { type AddressValues };

export const EMPTY_ADDRESS: AddressValues = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
};

/** Build a flat address string from structured fields for backward compatibility */
export function flattenAddress(v: AddressValues): string {
  return [v.addressLine1, v.addressLine2, v.city, v.state, v.country, v.postalCode]
    .filter(Boolean).join(', ');
}

/** Parse a flat address string into structured fields (best-effort) */
export function parseAddress(flat?: string, existing?: Partial<AddressValues>): AddressValues {
  // Structured fields are authoritative — prefer them whenever any are present
  // (e.g. `country` saved at signup without a street address).
  const structured: AddressValues = {
    addressLine1: existing?.addressLine1 || '',
    addressLine2: existing?.addressLine2 || '',
    city: existing?.city || '',
    state: existing?.state || '',
    country: existing?.country || '',
    postalCode: existing?.postalCode || '',
  };
  if (Object.values(structured).some(Boolean)) {
    return structured;
  }
  if (!flat) return { ...EMPTY_ADDRESS };
  // Fallback: put everything in addressLine1
  return {
    ...EMPTY_ADDRESS,
    addressLine1: flat,
  };
}

export default function StructuredAddressForm({ values, onChange, onBlur, disabled, showIcons }: Props) {
  const IconWrap = showIcons ? Home : 'div';
  const iconProps = showIcons ? { className: 'h-3.5 w-3.5 text-slate-400' as any, 'aria-hidden': true } : {};

  const set = (field: keyof AddressValues, val: string) => {
    onChange({ ...values, [field]: val });
  };

  const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-brand-primary transition-all disabled:bg-slate-50 disabled:cursor-not-allowed';

  return (
    <div className="space-y-3">
      {/* Address Line 1 */}
      <div>
        <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">
          Address Line 1 <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={values.addressLine1}
          onChange={e => set('addressLine1', e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          placeholder="Street address, P.O. box"
          className={inputCls}
        />
      </div>

      {/* Address Line 2 */}
      <div>
        <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Address Line 2 <span className="text-slate-300 font-normal">(optional)</span></label>
        <input
          type="text"
          value={values.addressLine2}
          onChange={e => set('addressLine2', e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          placeholder="Apartment, suite, unit, building, floor"
          className={inputCls}
        />
      </div>

      {/* City + State */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">
            City <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={values.city}
            onChange={e => set('city', e.target.value)}
            onBlur={onBlur}
            disabled={disabled}
            placeholder="City"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">State / Province / Region</label>
          <input
            type="text"
            value={values.state}
            onChange={e => set('state', e.target.value)}
            onBlur={onBlur}
            disabled={disabled}
            placeholder="State / Province"
            className={inputCls}
          />
        </div>
      </div>

      {/* Country + Postal */}
      <div className="grid grid-cols-2 gap-3">
        <CountrySelect
          value={values.country}
          onChange={v => set('country', v)}
          label="Country"
          required
          className=""
        />
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Postal / ZIP Code <span className="text-slate-300 font-normal">(optional)</span></label>
          <input
            type="text"
            value={values.postalCode}
            onChange={e => set('postalCode', e.target.value)}
            onBlur={onBlur}
            disabled={disabled}
            placeholder="Postal / ZIP"
            className={inputCls}
          />
        </div>
      </div>

      {showIcons && (
        <div className="flex items-start gap-1.5 mt-1">
          <MapPin className="h-3 w-3 text-slate-400 shrink-0 mt-px" />
          <p className="text-[10px] text-slate-500 leading-relaxed">
            This is where we can send program-related documents if needed.
          </p>
        </div>
      )}
    </div>
  );
}
